import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/Card';
import { Table, Column } from '../../components/Table';
import { Pagination } from '../../components/Pagination';
import type { DirectoryWorkspaceListItem, DirectoryWorkspaceListResponse } from '../../types/api';
import { useAdminResource } from './useAdminResource';
import { WorkspaceActions } from './WorkspaceManagement';
import { Chip, IdentityCell, RelativeTime, StatusPill } from '../../components/admin/TableCells';
import { DirectoryError, DirectorySearch, pageFromParam } from './parts';
import { DIRECTORY_PAGE_SIZE, humanize } from './labels';
import { PageHeader, TableSkeleton } from '../../components/admin/AdminUI';

function PlanCell({ row }: { row: DirectoryWorkspaceListItem }) {
  if (!row.plan) return <span className="text-slate-500">No plan record</span>;
  const differs = row.plan.onRecordCode && row.plan.onRecordCode !== row.plan.code;
  return (
    <div className="flex flex-col">
      <span>
        {row.plan.name ?? '—'}
        {row.subscriptionStatus && (
          <span className="ml-2 text-slate-500">({humanize(row.subscriptionStatus)})</span>
        )}
      </span>
      {differs && (
        <span className="text-xs text-slate-500">On record: {row.plan.onRecordName ?? row.plan.onRecordCode}</span>
      )}
    </div>
  );
}

/**
 * Admin console: every workspace, newest first, searchable by name or id.
 * Read-only.
 */
export function WorkspacesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const q = params.get('q') ?? '';
  const page = pageFromParam(params.get('page'));

  const query = new URLSearchParams();
  if (q) query.set('q', q);
  query.set('page', String(page));
  query.set('limit', String(DIRECTORY_PAGE_SIZE));
  const { data, error, loading, reload } = useAdminResource<DirectoryWorkspaceListResponse>(
    `/workspaces?${query.toString()}`,
  );

  const backTo = { from: location.pathname + location.search };
  const open = (row: DirectoryWorkspaceListItem) => navigate(`/workspaces/${row.id}`, { state: backTo });

  const setSearch = (next: { q?: string; page?: number }) => {
    const p = new URLSearchParams();
    const nextQ = next.q ?? q;
    if (nextQ) p.set('q', nextQ);
    const nextPage = next.page ?? 1;
    if (nextPage > 1) p.set('page', String(nextPage));
    setParams(p);
  };

  const columns: Column<DirectoryWorkspaceListItem>[] = [
    {
      key: 'name',
      header: 'Workspace',
      className: 'min-w-[15rem]',
      render: (row) => (
        <IdentityCell
          id={row.id}
          name={row.name}
          square
          primary={
            <Link
              to={`/workspaces/${row.id}`}
              state={backTo}
              onClick={(e) => e.stopPropagation()}
              className="hover:text-emerald-700 hover:underline"
            >
              {row.name}
            </Link>
          }
          secondary={`${row.type === 'personal' ? 'Personal' : 'Business'} workspace`}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {row.status === 'active' ? <StatusPill tone="green" label="Active" /> : <StatusPill tone="red" label={humanize(row.status)} />}
          {!row.onboardingFinished && <Chip tone="amber" label="Setup unfinished" />}
        </div>
      ),
    },
    { key: 'ownerEmail', header: 'Owner', render: (row) => (row.ownerEmail ? <span className="text-slate-700">{row.ownerEmail}</span> : <span className="text-slate-400">—</span>) },
    { key: 'memberCount', header: 'Members', className: 'text-right tabular-nums', render: (row) => <span className="font-medium text-slate-900">{row.memberCount}</span> },
    { key: 'plan', header: 'Plan', render: (row) => <PlanCell row={row} /> },
    { key: 'createdAt', header: 'Created', render: (row) => <RelativeTime iso={row.createdAt} never="—" /> },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <WorkspaceActions
          workspace={row}
          onChanged={reload}
          currentPlanCode={row.plan?.onRecordCode ?? row.plan?.code ?? null}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workspaces"
        subtitle={<>Every workspace in Central. Open one to manage its members, apps and plan, or use a row's actions.</>}
      />

      <Card>
        <DirectorySearch
          label="Search workspaces"
          placeholder="Search by workspace name or ID"
          value={q}
          onSearch={(next) => setSearch({ q: next, page: 1 })}
        />
      </Card>

      <Card>
        {loading ? (
          <TableSkeleton message="Loading workspaces…" />
        ) : error ? (
          <DirectoryError error={error} what="Workspaces" onRetry={reload} />
        ) : data ? (
          <>
            <p className="text-sm text-slate-500 mb-3" data-testid="directory-count">
              {q
                ? `${data.pagination.total} ${data.pagination.total === 1 ? 'workspace matches' : 'workspaces match'} “${q}”`
                : `${data.pagination.total} ${data.pagination.total === 1 ? 'workspace' : 'workspaces'}`}
            </p>
            <Table<DirectoryWorkspaceListItem>
              columns={columns}
              data={data.workspaces}
              keyField="id"
              onRowClick={open}
              emptyMessage={q ? `No workspaces match “${q}”.` : 'No workspaces yet.'}
            />
            <Pagination
              total={data.pagination.total}
              limit={data.pagination.limit}
              offset={(data.pagination.page - 1) * data.pagination.limit}
              onPageChange={(offset) =>
                setSearch({ page: Math.floor(offset / data.pagination.limit) + 1 })
              }
            />
          </>
        ) : null}
      </Card>
    </div>
  );
}
